<template>
  <SettingsRow
    name="Call recording & transcripts"
    description="Records calls for coaching, transcripts and summaries. Skip the places where all-party consent is required: US states and Canadian provinces one by one, other countries as a whole."
  >
    <ToggleSwitch :model-value="form.recordingEnabled" label="Record calls" @update:model-value="onRecordingToggle" />
    <div v-if="form.recordingEnabled" class="excludeBlock">
      <span class="excludeLabel">Don’t record calls to</span>
      <span v-for="code in form.excludedRegions" :key="code" class="regionChip">
        {{ regionLabel(code) }}
        <button type="button" class="chipX" :aria-label="`Remove ${regionLabel(code)}`" @click="removeRegion(code)">×</button>
      </span>
      <select
        class="addRegionSel"
        aria-label="Add a region"
        :value="''"
        @change="onAddRegion(($event.target as HTMLSelectElement).value)"
      >
        <option value="">+ Add</option>
        <optgroup label="US states">
          <option v-for="r in availableUsRegions" :key="r.code" :value="r.code">{{ r.name }}</option>
        </optgroup>
        <optgroup label="CA provinces">
          <option v-for="r in availableCaRegions" :key="r.code" :value="r.code">{{ r.name }}</option>
        </optgroup>
        <optgroup v-if="availableCountries.length" label="Other countries">
          <option v-for="r in availableCountries" :key="r.code" :value="r.code">{{ r.name }}</option>
        </optgroup>
      </select>
    </div>
  </SettingsRow>

  <SettingsRow
    name="Calling hours"
    description="The agent only places calls inside these hours, in each prospect’s local time."
  >
    <div class="weekChips" role="group" aria-label="Calling days">
      <button
        v-for="d in WEEKDAYS"
        :key="d.key"
        type="button"
        class="weekChip"
        :class="{ on: form.days.includes(d.key) }"
        :aria-pressed="form.days.includes(d.key)"
        :title="d.full"
        @click="toggleDay(d.key)"
      >{{ d.letter }}</button>
    </div>
    <div class="timeRow">
      <select v-model="form.start" class="tinput timeSel" aria-label="Start time" @change="onHoursChange">
        <option v-for="o in TIME_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>
      <span class="toLabel">to</span>
      <select v-model="form.end" class="tinput timeSel" aria-label="End time" @change="onHoursChange">
        <option v-for="o in TIME_OPTIONS" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>
      <span v-if="form.end < form.start" class="nextDay">(next day)</span>
    </div>
    <p v-if="validationError" class="errorMsg">{{ validationError }}</p>
  </SettingsRow>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ToggleSwitch from '../../../../components/ToggleSwitch.vue'
import SettingsRow from './SettingsRow.vue'
import { useTelephony } from '../../../composables/useTelephony'
import { useAutosave } from '../../../composables/useAutosave'
import {
  WEEKDAYS,
  TIME_OPTIONS,
  DEFAULT_GUARDRAILS,
  RECORDING_REGION_OPTIONS,
  recordingCountryOptions,
  regionLabel,
  type GuardrailsShape,
} from './telephonyOptions'
import { putGuardrails } from '../../../../services/dialerApi'
import { APIError } from '../../../../services/api'

const telephony = useTelephony()

const form = ref<GuardrailsShape>({ ...DEFAULT_GUARDRAILS, days: [...DEFAULT_GUARDRAILS.days], excludedRegions: [] })
const validationError = ref('')
// Unsaved hours stay NULL until the admin touches them.
const hoursEdited = ref(false)

const { status, errorMessage, saveNow } = useAutosave(persist)
// The page renders these in the section header (AutosaveBadge).
defineExpose({ status, errorMessage, retry: saveNow })

function hydrateFromProjection() {
  // Local edits that haven't landed win over whatever the server last said.
  if (status.value === 'saving' || status.value === 'error' || validationError.value) return
  const g = telephony.projection.value?.guardrails
  if (g?.calling_hours) {
    form.value = {
      days: [...g.calling_hours.days],
      start: g.calling_hours.start,
      end: g.calling_hours.end,
      recordingEnabled: g.recording.enabled,
      excludedRegions: [...g.recording.excluded_regions],
    }
  } else {
    // No saved hours → pre-fill defaults, but honor the real recording flag.
    form.value = {
      ...DEFAULT_GUARDRAILS,
      days: [...DEFAULT_GUARDRAILS.days],
      recordingEnabled: g?.recording.enabled ?? DEFAULT_GUARDRAILS.recordingEnabled,
      excludedRegions: g?.recording.excluded_regions ? [...g.recording.excluded_regions] : [],
    }
  }
}

const availableUsRegions = computed(() =>
  RECORDING_REGION_OPTIONS.filter((r) => r.country === 'US' && !form.value.excludedRegions.includes(r.code)),
)
const availableCaRegions = computed(() =>
  RECORDING_REGION_OPTIONS.filter((r) => r.country === 'CA' && !form.value.excludedRegions.includes(r.code)),
)
// The destinations the tenant may dial outside US & Canada (plus any already excluded).
const availableCountries = computed(() =>
  recordingCountryOptions(telephony.projection.value?.guardrails.recording.countries ?? []).filter(
    (r) => !form.value.excludedRegions.includes(r.code),
  ),
)

function validate(): string {
  if (!form.value.days.length) return 'Pick at least one calling day.'
  // A later end than start wraps past midnight, which the dialer supports.
  if (form.value.start === form.value.end) return 'Start and end can’t be the same time.'
  return ''
}

function commit(kind: 'hours' | 'recording'): void {
  validationError.value = validate()
  if (validationError.value && kind === 'hours') return
  void saveNow()
}

function toggleDay(key: string) {
  const set = new Set(form.value.days)
  if (set.has(key)) set.delete(key)
  else set.add(key)
  form.value.days = WEEKDAYS.filter((d) => set.has(d.key)).map((d) => d.key)
  onHoursChange()
}

function onHoursChange() {
  hoursEdited.value = true
  commit('hours')
}

function onRecordingToggle(on: boolean) {
  form.value.recordingEnabled = on
  commit('recording')
}

function onAddRegion(code: string) {
  if (!code || form.value.excludedRegions.includes(code)) return
  form.value.excludedRegions = [...form.value.excludedRegions, code]
  commit('recording')
}
function removeRegion(code: string) {
  form.value.excludedRegions = form.value.excludedRegions.filter((c) => c !== code)
  commit('recording')
}

async function persist() {
  const hoursValid = !validate()
  const sendHours = hoursValid && (hoursEdited.value || !!telephony.projection.value?.guardrails.calling_hours)
  try {
    const projection = await putGuardrails({
      ...(sendHours ? { calling_hours: { days: form.value.days, start: form.value.start, end: form.value.end } } : {}),
      recording: { enabled: form.value.recordingEnabled, excluded_regions: form.value.excludedRegions },
    })
    telephony.applyProjection(projection)
  } catch (e) {
    console.warn('[GuardrailsSurface] save failed', e)
    // A 422 carries a readable reason; anything else gets plain copy.
    if (e instanceof APIError && e.status === 422) throw e
    throw new Error('Couldn’t save your changes')
  }
}

onMounted(hydrateFromProjection)
watch(() => telephony.projection.value?.guardrails, hydrateFromProjection)
</script>

<style scoped>
.excludeBlock { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); }
.excludeLabel { font-size: var(--fs-sm); color: var(--color-text-secondary); margin-right: var(--space-1); }
.regionChip {
  display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-full); padding: 3px 8px 3px 12px; font-size: var(--fs-xs); font-weight: var(--fw-medium);
}
.chipX { border: 0; background: transparent; padding: 0; color: var(--color-text-tertiary); cursor: pointer; font-size: 14px; line-height: 1; }
.addRegionSel {
  border: 1.5px dashed var(--color-border-strong); border-radius: var(--radius-full); padding: 3px 12px;
  font-size: var(--fs-xs); font-weight: var(--fw-medium); color: var(--color-text-tertiary); background: transparent; cursor: pointer;
}

.weekChips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.weekChip {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  border: 0; border-radius: var(--radius-full); background: var(--color-bg-sunk); color: var(--color-text-tertiary);
  font-size: var(--fs-xs); font-weight: var(--fw-semibold); cursor: pointer;
}
.weekChip.on { background: var(--color-accent); color: #fff; }

.timeRow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.timeSel { width: auto; min-width: 120px; }
.toLabel, .nextDay { font-size: var(--fs-sm); color: var(--color-text-tertiary); }
.errorMsg { margin: 0; font-size: var(--fs-xs); color: var(--color-danger); }
</style>
